import * as React from 'react'

import { useLocation, useNavigate } from 'react-router-dom'
import { type StringPaths, type FunctionPaths } from '../paths'
import { isPreview } from './useDisplayMode'

/**
 * Given the fact that we use browser history based routing, we do not have access to routes with a hash natively.
 * Still, we need to prepend the hash on some routes to exclude them from search engine crawlers.
 * This hook is here because of that, otherwise the browser would just 'ignore' the part after the hash
 * assuming it is a local anchor.
 */
export function useHashRouting(): void {
  const location = useLocation()

  React.useEffect(() => {
    // Now that we use react-router v6, everything related to hash navigation
    // is done somewhere else (in the `TabRouter.tsx` file). These conditions
    // are no more needed (except the last one). In order to avoid regressions
    // on the condition for the display of the share modal, the useless
    // conditions are preserved with empty blocks (thus preserving the logic).
    if (location.hash.includes('#comments')) {
      // NOOP
    } else if (location.hash.includes('#faq')) {
      // NOOP
    } else if (location.hash.includes('#rewards')) {
      // NOOP
    }
  }, [location.hash])
}

type UseNavigation = {
  is: (path: StringPaths) => boolean
  to: (path: StringPaths, state?: Record<string, unknown>) => void
  pathTo: (path: StringPaths | FunctionPaths) => { pathname: string; search?: string; hash?: string }
}

export function useNavigation(): UseNavigation {
  const location = useLocation()
  const navigate = useNavigate()

  return { is, to, pathTo }

  function is(path: StringPaths): boolean {
    const pathname = pathTo(path).pathname
    const hash = pathTo(path).hash

    let comparedPathname = pathname
    if (hash) {
      comparedPathname = comparedPathname + hash
    }

    // compare without trailing slash
    let currentLocation = location.pathname
    if (['#comments/', '#faq/', '#rewards/'].includes(location.hash)) {
      currentLocation = location.pathname + location.hash
    }

    return currentLocation.replace(/\/$/, '') === comparedPathname.replace(/\/$/, '')
  }

  function to(path: StringPaths, state?: Record<string, unknown>): void {
    const routerPath = pathTo(path)
    navigate(`${routerPath.pathname}${routerPath.hash ?? ''}`, { state })
  }

  function pathTo(path: StringPaths | FunctionPaths): { pathname: string; search?: string; hash?: string } {
    const pathname = typeof path === 'string' ? path : path.path(path.params)

    const hashIndex = pathname.indexOf('#')
    const hash = pathname.slice(hashIndex)

    if (isPreview(location.pathname)) {
      if (hashIndex === -1) {
        return {
          pathname: `${pathname}preview/`,
          search: location.search,
        }
      }

      if (['/#comments/', '/#faq/', '/#rewards/'].includes(pathname)) {
        return {
          pathname: `/preview/`,
          hash,
          search: location.search,
        }
      }

      return {
        pathname: `${pathname}preview/`,
        hash,
        search: location.search,
      }
    }

    if (['/#comments/', '/#faq/', '/#rewards/'].includes(pathname)) {
      return {
        hash,
        pathname: '/',
      }
    }

    return { pathname }
  }
}
